import {api} from "@/lib/api";

export interface ChatGroupSummary {
    groupId: string;
    name: string;
    channelId: string;
}

export interface ChatDmSummary {
    channelId: string;
    otherUserId: string;
    name: string;
}

/** A conversation-list row: a group channel or a direct-message channel. */
export type ChatConversation =
    | {kind: "group"; channelId: string; name: string}
    | {kind: "dm"; channelId: string; name: string; otherUserId: string};

/**
 * Mint an emb.chat connect token for the logged-in user (signed by our backend).
 *
 * expiresInSeconds is optional so an app build running against a backend that predates it still
 * type-checks; the chat client falls back to a short assumed lifetime when it is missing.
 */
export async function getChatToken(): Promise<{token: string; expiresIn: string; expiresInSeconds?: number}> {
    const xhr = await api.post<{token: string; expiresIn: string; expiresInSeconds?: number}>("/chat/token");
    return xhr.data;
}

/** The chat-active groups in an org that the user belongs to (drives the channel list). */
export async function getChatGroups(orgId: string): Promise<ChatGroupSummary[]> {
    const xhr = await api.get<{groups: ChatGroupSummary[]}>("/chat/groups", {params: {orgId}});
    return xhr.data.groups;
}

/** The caller's direct-message conversations within an org (DMs are per-organization). */
export async function getChatDms(orgId: string): Promise<ChatDmSummary[]> {
    const xhr = await api.get<{dms: ChatDmSummary[]}>("/chat/dms", {params: {orgId}});
    return xhr.data.dms;
}

/**
 * Get-or-create a DM channel with another member of a group in this org; returns its channel id.
 * The same pair has an independent thread per organization.
 */
export async function startDm(orgId: string, otherUserId: string): Promise<{channelId: string}> {
    const xhr = await api.post<{channelId: string}>("/chat/dms", {orgId, otherUserId});
    return xhr.data;
}
